<script setup lang="ts">
  import {
    FormFieldContainer,
    InputField,
    SwitchButton,
    type TextElement,
  } from '@logistiek-zonder-papier/lzp-library'
  import { reactive } from 'vue'
  import FlowPathInput from '@/components/form/FlowPathInput.vue'

  const field = defineModel<TextElement>({ required: true })
  const touched = reactive({
    storeLocation: false,
  })

  function validateDto() {
    return touched.storeLocation &&
      field.value?.type === 'text' &&
      !field.value?.storeLocation?.trim()
      ? 'DTO Target Path is required'
      : null
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Label">
      <FlowPathInput
        placeholder="e.g. ${customer.name}"
        hint="Field label. Supports ${...} interpolation."
        v-model="field.label"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Placeholder">
      <FlowPathInput
        placeholder="Placeholder text"
        hint="Grey hint inside the field. Supports ${...} interpolation."
        v-model="field.placeholder"
        :optional="true"
        type="text"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Default value">
      <FlowPathInput
        placeholder="Initial value"
        v-model="field.defaultValue"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Store Location" :error="validateDto()">
      <InputField
        placeholder="x.path.to.location"
        hint="Target path url."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Required">
      <SwitchButton v-model="field.required" size="md" />
    </FormFieldContainer>

    <FormFieldContainer label="Enable Minimum Character Limit">
      <SwitchButton
        :modelValue="field.minimumCharacterLimit !== null"
        @update:model-value="
          value => {
            field.minimumCharacterLimit = value ? '0' : null
          }
        "
        size="md"
      />
    </FormFieldContainer>

    <FormFieldContainer
      v-if="field.minimumCharacterLimit != null"
      label="Minimum Character Limit
      (inclusive)"
    >
      <FlowPathInput
        placeholder="Minimum number of characters required"
        v-model="field.minimumCharacterLimit"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Enable Maximum Character Limit">
      <SwitchButton
        :modelValue="field.maximumCharacterLimit !== null"
        @update:model-value="
          value => {
            field.maximumCharacterLimit = value ? '0' : null
          }
        "
        size="md"
      />
    </FormFieldContainer>

    <FormFieldContainer
      v-if="field.maximumCharacterLimit != null"
      label="Maximum Character Limit
      (inclusive)"
    >
      <FlowPathInput
        placeholder="How many characters are allowed"
        v-model="field.maximumCharacterLimit"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Description">
      <FlowPathInput
        placeholder="Helpful text under the field"
        v-model="field.description"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Error message">
      <FlowPathInput
        placeholder="Error message under the field"
        v-model="field.error"
        :optional="true"
        :validate-paths="true"
      />
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .two-columns {
    display: flex;
    gap: 0.75rem;
  }

  .row {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
</style>
